import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { SelectOfficeMulti } from '@/components/select-office/select-office-multi';
import {
  Form,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { DatePickerForm } from '../datepicker/datepicker-form';
import { SelectDoctorAttendingMulti } from './select-doctor-attending-multi';
import { ExportAccountingTransferOrderUseForm } from './use-export-accounting-transfer-order-form';

type Props = {
  form: ExportAccountingTransferOrderUseForm;
};

const ExportAccountingTransferOrderForm = ({ form }: Props) => {
  const { t } = useTranslation();
  const officeIds = form.watch('officeIds') ?? [];
  const singleOfficeId = officeIds.length === 1 ? officeIds[0]! : null;

  // Clear doctor selection whenever the effective office selection changes,
  // including single-office A -> single-office B transitions, to avoid
  // carrying stale doctor IDs over to a different office.
  useEffect(() => {
    form.setValue('doctorAttendingIds', undefined);
  }, [singleOfficeId, form]);

  return (
    <Form {...form}>
      <form className="grid gap-4">
        <DatePickerForm
          form={form}
          fieldName="period"
          captionLayout="dropdown"
          inline={false}
          mode="range"
          align="center"
          label={t('export-accounting-transfer-order-form.period.label')}
          placeholder={t(
            'export-accounting-transfer-order-form.period.placeholder',
          )}
        />

        <FormField
          control={form.control}
          name="officeIds"
          render={({ field }) => (
            <FormItem>
              <div className="flex flex-col gap-2">
                <FormLabel>
                  {t('export-accounting-transfer-order-form.office.label')}
                </FormLabel>

                <SelectOfficeMulti
                  value={field.value ?? []}
                  onValueChange={(ids) => {
                    form.setValue('officeIds', ids, {
                      shouldValidate: true,
                    });
                  }}
                />
              </div>

              <FormDescription>
                {t('export-accounting-transfer-order-form.office.description')}
              </FormDescription>

              <FormMessage />
            </FormItem>
          )}
        />

        {singleOfficeId !== null && (
          <FormField
            control={form.control}
            name="doctorAttendingIds"
            render={({ field }) => (
              <FormItem>
                <div className="flex flex-col gap-2">
                  <FormLabel>
                    {t(
                      'export-accounting-transfer-order-form.doctor-attending.label',
                    )}
                  </FormLabel>

                  <SelectDoctorAttendingMulti
                    officeId={singleOfficeId}
                    value={field.value}
                    onValueChange={(ids) => {
                      form.setValue('doctorAttendingIds', ids, {
                        shouldValidate: true,
                      });
                    }}
                  />
                </div>

                <FormDescription>
                  {t(
                    'export-accounting-transfer-order-form.doctor-attending.description',
                  )}
                </FormDescription>

                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </form>
    </Form>
  );
};

export default ExportAccountingTransferOrderForm;
